<template>
  <div class="git-history-wrapper">
    <template v-if="collapsibleCard">
      <rbz-card-collapsible
        v-model:isCollapsed="isCollapsed"
        @update:isCollapsed="$event ? null : loadGitLog($event)"
        data-qa="git-history-card"
      >
        <template #title>
          {{ t('versionControl.history.cardTitle') }}
        </template>
        <template #content>
          <rbz-loader :loading="!!isLoading" :data-exists="!!gitLog?.length" height="220px" />
          <rbz-table
            :columns="columns"
            :table-data="gitLog"
            :row-buttons="rowButtons"
            default-sort-column-direction="desc"
            data-qa="git-history-table"
            :show-buttons-column="!disabled"
            rows-per-page="20"
            :show-new-button="false"
            :show-download-button="false"
            :show-filter-button="showFilterButton"
            @restore-row-button-clicked="restoreVersion"
          >
          </rbz-table>
        </template>
      </rbz-card-collapsible>
    </template>
    <template v-else>
      <rbz-loader :loading="!!isLoading" :data-exists="!!gitLog?.length" height="220px" />
      <rbz-table
        :columns="columns"
        :table-data="gitLog"
        :row-buttons="rowButtons"
        default-sort-column-direction="desc"
        data-qa="git-history-table"
        rows-per-page="100"
        :show-new-button="false"
        :show-download-button="false"
        :show-filter-row="true"
        :disabled="disabled"
        :show-filter-button="showFilterButton"
        @restore-row-button-clicked="restoreVersion"
      >
      </rbz-table>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { Commit } from '@/types';
import RequestsUtils from '@/helpers/RequestsUtils';
import axios, { AxiosError } from 'axios';
import RbzLoader from '@/components/RbzLoader.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import DateTimeUtils from 'ui-component-library/helpers/DateTimeUtils';
import RbzCardCollapsible from 'ui-component-library/rbz/components/RbzCardCollapsible.vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/store/UserStore';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';

const { t } = useI18n();

interface GitHistory {
  collapsibleCard?: boolean;
  apiPath: string;
  restoreTargetTitle: string;
  apiRestorePath?: string;
  isCollapsed?: boolean;
  showFilterButton?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<GitHistory>(), {
  collapsibleCard: true,
  isCollapsed: true,
  showFilterButton: false,
  disabled: false,
});

const gitLog = ref<Commit[]>([]);
const isCollapsed = ref(props.isCollapsed);
const isLoading = ref(false);
const columns = ref<RbzTableColumnOptions[]>([
  {
    id: 'date',
    title: t('versionControl.history.date'),
    fieldNames: ['date'],
    displayFunction: (item: Commit) => {
      return DateTimeUtils.isoToNowCuriefenseFormat(item.date);
    },
    tooltipFunction: (item: Commit) => {
      return DateTimeUtils.isoToNowFullCuriefenseFormat(item.date);
    },
    isSortable: true,
    isSearchable: true,
    isSortByOriginalValue: true,
    classes: 'width-200px',
    cellContentClasses: 'ellipsis',
    autoFocus: true,
  },
  {
    id: 'version',
    title: t('versionControl.history.version'),
    fieldNames: ['version'],
    displayFunction: (item: Commit) => {
      return item.version?.substring(0, 7) || '';
    },
    tooltipFunction: (item: Commit) => {
      return item.version;
    },
    isSortable: true,
    isSearchable: true,
    isSortByOriginalValue: true,
    classes: 'width-100px',
    cellContentClasses: 'ellipsis',
  },
  {
    id: 'parents',
    title: t('versionControl.history.parents'),
    fieldNames: ['parents'],
    displayFunction: (item: Commit) => {
      let returnString = '';
      item.parents.forEach((parent: string) => {
        if (returnString) {
          returnString += '\n';
        }
        returnString += parent.substr(0, 7) || '';
      });
      return returnString;
    },
    tooltipFunction: (item: Commit) => {
      let returnString = '';
      item.parents.forEach((parent: string) => {
        if (returnString) {
          returnString += '\n';
        }
        returnString += parent;
      });
      return returnString;
    },
    isSearchable: true,
    classes: 'width-100px',
    cellContentClasses: 'ellipsis',
  },
  {
    id: 'message',
    title: t('versionControl.history.message'),
    fieldNames: ['message'],
    isSortable: true,
    isSearchable: true,
    cellContentClasses: 'ellipsis',
  },
  {
    id: 'author',
    title: t('versionControl.history.author'),
    fieldNames: ['author'],
    classes: 'width-150px',
    isSortable: true,
    isSearchable: true,
    cellContentClasses: 'ellipsis',
  },
  {
    id: 'email',
    title: t('versionControl.history.email'),
    fieldNames: ['email'],
    classes: 'width-200px',
    isSortable: true,
    isSearchable: true,
    cellContentClasses: 'ellipsis',
  },
]);
const rowButtons = ref<RbzTableRowButton[]>([
  {
    id: 'restore',
    tooltip: t('versionControl.history.restore.tableTooltip'),
    icon: 'IconRestore',
  },
]);
let cancelSource = axios.CancelToken.source();

interface gitHistoryEmits {
  (e: 'restore-version'): void;
}

const emit = defineEmits<gitHistoryEmits>();
const userStore = useUserStore();

const loadGitLog = async () => {
  if (isCollapsed.value) {
    return;
  }
  isLoading.value = true;
  gitLog.value = [];
  const url = props.apiPath;
  cancelSource.cancel('Operation cancelled because a new search has began');
  cancelSource = axios.CancelToken.source();
  const config = {
    cancelToken: cancelSource.token,
  };
  const response = await RequestsUtils.sendRequest({
    host: userStore.getUserPlanet(),
    methodName: 'GET',
    url: url,
    config: config,
  }).catch((err: AxiosError) => {
    console.error(
      `Error while attempting to load Git Log from path: ${props.apiPath}. Error: ${err}`
    );
  });
  if (response) {
    gitLog.value = response.data?.items || [];
    gitLog.value.forEach((commit: Commit) => {
      commit.id = commit.version;
    });
  }
  isLoading.value = false;
};

loadGitLog();
watch(
  () => props.apiPath,
  async () => {
    loadGitLog();
  }
);

const restoreVersion = async (commit: Commit) => {
  const versionId: Commit['id'] = commit.id;
  const url = props.apiRestorePath ? props.apiRestorePath : props.apiPath;
  const successMessage = t('versionControl.history.restore.messages.success', {
    entityName: props.restoreTargetTitle,
    version: versionId,
  });
  const failureMessage = t('versionControl.history.restore.messages.failure', {
    entityName: props.restoreTargetTitle,
    version: versionId,
  });
  await RequestsUtils.sendRequest({
    methodName: 'PUT',
    url: `${url}/${versionId}/revert`,
    successMessage,
    failureMessage,
  });
  loadGitLog();
  emit('restore-version');
};

defineExpose({
  loadGitLog,
});
</script>
<style scoped lang="scss">
.git-history-wrapper:not(:first-child) {
  margin-top: 24px;
}
</style>
